/**
 * JS-1704 — project dashboard tab selection and split-per-date view.
 *
 * The dashboard used to hold a single `tab` string. It now holds a list, because the tab
 * bar is multi-select (open + ongoing, say). Keeping the rules here — free of Angular and
 * of the component's 1200 lines of table/scroll plumbing — is what makes them testable
 * (tests/project-dashboard-tabs.rules.spec.ts).
 */

/**
 * Every pill on the bar, in the order they are rendered. A selection is always sorted into
 * this order, so `tabs[0]` is stable, the `?tabs=` param reads the same way the bar does,
 * and two selections with the same pills are the same string.
 */
export const PROJECT_DASHBOARD_TABS: Array<string> = [
  'open',
  'ongoing',
  'past',
  'draft project',
  'archived',
  'cancelled',
  'draft',
  'submitted',
];

/**
 * Where the bar sits with no filter on it: one status, the one it opens on. Clearing the
 * status filter returns here rather than to an empty selection — the api has no view for
 * "no status at all", which is the same reason `toggleTab` refuses to clear the last one.
 */
export const DEFAULT_DASHBOARD_TABS: Array<string> = ['open'];

/** The selection the Clear button restores. A fresh array — callers mutate what they get. */
export function clearedTabs(): Array<string> {
  return DEFAULT_DASHBOARD_TABS.slice();
}

/**
 * The translation key each pill prints, keyed by the tab value the api is asked for. The
 * two are not the same word — `draft` is the orders pill and `draft project` the projects
 * one — so the status filter cannot build a label out of the value it sends. Both the bar
 * and the filter modal read their labels from here, which is what keeps them in step.
 */
export const PROJECT_DASHBOARD_TAB_LABELS: { [tab: string]: string } = {
  open: 'open projects',
  ongoing: 'ongoing projects',
  past: 'past projects',
  'draft project': 'draft projects',
  archived: 'archived projects',
  cancelled: 'cancelled projects',
  draft: 'draft orders',
  submitted: 'submited orders',
};

/**
 * JS-1704 — what the status filter offers, and what it calls it.
 *
 * Andrea, 28.09: the modal is for the PROJECT status only — "we will only filter for
 * projects, not in orders, only for draft projects" — so the two order pills are left out
 * of it entirely. Order is the dashboard's own, not alphabetical.
 */
export const PROJECT_DASHBOARD_FILTER_TABS: Array<string> = [
  'open',
  'ongoing',
  'past',
  'draft project',
  'archived',
  'cancelled',
];

/**
 * The modal's labels, which are NOT the pills'. The subtitle now carries the word
 * "project", so each option no longer repeats it. `draft project` can safely shorten to
 * "draft" because the order pills — the other thing that would claim that word — are not
 * offered here.
 */
export const PROJECT_DASHBOARD_FILTER_LABELS: { [tab: string]: string } = {
  open: 'open',
  ongoing: 'ongoing',
  past: 'past',
  'draft project': 'draft',
  archived: 'archived',
  cancelled: 'cancelled',
};

/** The key the filter modal translates a tab under; falls back to the pill's own label. */
export function filterLabelKey(tab: string): string {
  return PROJECT_DASHBOARD_FILTER_LABELS[tab] || tabLabelKey(tab);
}

/**
 * The selection the modal opens with. Only what the modal itself offers survives: the bar
 * can be sitting on an order pill, and the modal has no box to represent that, so carrying
 * it into the draft would silently reapply it on Apply.
 */
export function filterableTabs(tabs: Array<string>): Array<string> {
  return PROJECT_DASHBOARD_FILTER_TABS.filter((tab) => (tabs || []).indexOf(tab) > -1);
}

/**
 * The key a tab's label is translated under. An unknown tab falls back to its own value so
 * a pill added to the list without a label here still renders something readable.
 */
export function tabLabelKey(tab: string): string {
  return PROJECT_DASHBOARD_TAB_LABELS[tab] || tab;
}

/**
 * JS-1704 — is the status filter doing anything?
 *
 * Exactly one tab is the bar's resting state: that is a pill selection, not a filter, and
 * the Filter pill stays grey for it. More than one can only have come from the filter
 * modal, so the pill goes green and stays green while the combination holds.
 */
export function isTabCombination(tabs: Array<string>): boolean {
  return !!tabs && tabs.length > 1;
}

/**
 * JS-1704 — should this status pill render green?
 *
 * Only while it is the one selected status. Once the filter has put several statuses on,
 * the Filter pill is what reports that and the status pills all go quiet, the way the
 * employees dashboard behaves: six green pills read as six separate selections, not as one
 * filter, and the bar has no way to say which of the two it means.
 */
export function isPillActive(tabs: Array<string>, tab: string): boolean {
  return !isTabCombination(tabs) && !!tabs && tabs.indexOf(tab) > -1;
}

/** Drops unknown and duplicate tabs and puts the rest back in pill-bar order. */
export function orderTabs(tabs: Array<string>): Array<string> {
  return PROJECT_DASHBOARD_TABS.filter((tab) => tabs.indexOf(tab) > -1);
}

/**
 * Checkbox click on a pill: add the tab to the selection, or take it out again.
 *
 * The last selected tab cannot be unchecked — an empty selection would ask the api for
 * every project in the agency regardless of status, which is not a view the bar can show.
 */
export function toggleTab(selected: Array<string>, tab: string): Array<string> {
  if (PROJECT_DASHBOARD_TABS.indexOf(tab) === -1) {
    return orderTabs(selected);
  }

  const isSelected = selected.indexOf(tab) > -1;

  if (isSelected && selected.length === 1) {
    return orderTabs(selected);
  }

  const next = isSelected
    ? selected.filter((t) => t !== tab)
    : selected.concat([tab]);

  return orderTabs(next);
}

/**
 * Reads the selection back out of the `?tabs=` query param or the projectDBFilter
 * localStorage entry, either of which can be a comma-separated string, an array, or
 * absent. Anything unrecognisable falls back rather than leaving the bar with no
 * selection at all.
 */
export function normaliseTabs(
  value: any,
  fallback: Array<string> = DEFAULT_DASHBOARD_TABS
): Array<string> {
  const parts: Array<string> = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split(',')
    : [];

  const tabs = orderTabs(parts.map((part) => String(part).trim()));

  return tabs.length ? tabs : orderTabs(fallback);
}

/** The value sent to the api as `tabs=` and written to the url. */
export function tabsQueryValue(tabs: Array<string>): string {
  return tabs.join(',');
}

/**
 * JS-1494 point 2 made past projects default to newest first. That only makes sense while
 * past is the whole view: combined with a live tab, the list is chronological again.
 */
export function defaultSortAscending(tabs: Array<string>): boolean {
  return !(tabs.length === 1 && tabs[0] === 'past');
}

/** The split view sorts on the row's own date, not on the project's span. */
export function defaultSortColumn(splitByDate: boolean): string {
  return splitByDate ? 'date' : 'start_date';
}

/**
 * The split view shows one row per date, so the project's span collapses into that date:
 * the start-date column becomes the row date and the end-date column goes away. The status
 * column is keyed on `end_date` too but renders a badge, not a date — it has to survive.
 */
export function splitDateColumns(columns: Array<any>): Array<any> {
  return columns
    .filter((column) => !(column.data === 'end_date' && column.type === 'date'))
    .map((column) =>
      column.data === 'start_date' && column.type === 'date'
        ? { name: 'date', data: 'date', sort: true, type: 'date', hasQueryParams: true }
        : column
    );
}

/**
 * JS-1704 — a row belongs to a group when its project was expanded into more than one
 * date row. `date_row_count` counts the project's rows across the whole filtered set, not
 * the loaded page, so the grouping bar still shows on the last row of a page whose
 * siblings have not been scrolled in yet.
 */
export function isGroupedSplitRow(row: any): boolean {
  return !!(row && row.date && Number(row.date_row_count) > 1);
}

/**
 * JS-1704 — is this row a past project?
 *
 * The archive action belongs to a past project, not to the Past tab. While only one tab
 * could be selected the two said the same thing, so the table asked the tab. With a
 * combination — Open + Past, say — that puts an archive button on every open project in
 * the list too, which is why the question is now asked of the row.
 *
 * `past` is what the api's own tab predicate tests (`projects.type = 'past'`), so a row
 * that would answer the Past tab answers this the same way.
 */
export function isPastProjectRow(row: any): boolean {
  return !!(row && row.type === 'past');
}
